import {
    Box,
    createStyles,
    Grid,
    LinearProgress,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles,
} from "@material-ui/core";
import classNames from "classnames";
import React from "react";
import { Id } from "shared/src/models/framework/db";
import { Mode } from "shared/src/models/poll";
import { PollCard } from "shared/src/network/requests/poll/poll-req-models";
import { Analytic } from "../analytic/analytic";
import { HEIGHT_LOWER_THRESHOLD } from "../const";
import { commonClasses, spacing } from "../styles";
import { colors } from "../theme";
import { getMediaPath } from "../utils/media";
import Question from "./Question";
import Timer from "./Timer";
import User from "./User";
// import SharePopUpInPollCard from "./SharePopUpInPollCard";
// import { KeyboardArrowDownRounded, KeyboardArrowUpRounded } from "@material-ui/icons";

const styles = (theme: Theme) =>
    createStyles({
        ...commonClasses,
        ...spacing(theme),
        pollContainer: {
            overflow: "hidden",
            WebkitTapHighlightColor: "transparent",
            position: "relative",
        },
        videoContainer: {
            position: "absolute",
            height: "100%",
        },
        videoBackdrop: {
            width: "100%",
            height: "100%",
            position: "absolute",
            backgroundColor: "rgba(0,0,0,0.5)",
            // backdropFilter: "blur(10px)",
        },
        trackSwitch: {
            "& .Mui-checked": {
                color: colors.components.switch.red,
                "&+.MuiSwitch-track": {
                    backgroundColor: colors.components.switch.red,
                },
            },
        },
        deleteImage: {
            border: "1px solid rgb(204,204,204)",
            borderRadius: "50%",
            display: "block",
            cursor: "pointer",
            position: "relative",
            width: "20px",
        },
        shareCollapseButtonDiv: {
            position: "absolute",
            bottom: 4,
            marginLeft: "-23px",
        },
        optionMedia: {
            objectFit: "cover",
            width: `100%`,
            height: `100%`,
        },
    });

type Props = WithStyles<typeof styles> & {
    poll: Id<PollCard>;
    availableHeight?: number;
    onOptionSelected?: (pollId: string, optionId: string | null) => void;
    onCommentSend?: (pollId: string, comment: string) => Promise<void>;
    onReadCommentsClick?: (pollId: string) => void;
    onVideoEnded?: (pollId: string) => void;
    onTrackingChange?: (pollId: string, trackingStatus: boolean) => Promise<void>;
    remainingTime?: number;
    isFocused?: boolean;
    uid?: string;
    mode?: Mode;
    onPollDelete?: (pollId: string) => void;
    isDisplayDelete?: boolean;
    showUserGuide?: boolean;
    cancelCardAutoSwipe?: () => void;
};

type State = {
    hasVideoEnded: boolean;
    toggleRenderer: boolean;
    isTracking: boolean;
};

class Component extends React.Component<Props, State> {
    videoRef: React.RefObject<HTMLVideoElement>;

    constructor(props: Props) {
        super(props);
        this.videoRef = React.createRef();
        const { poll } = props;

        this.state = {
            hasVideoEnded: !poll.video,
            toggleRenderer: false,
            isTracking: !!props.poll.track,
        };
    }

    componentDidUpdate = async (prevProps: Props, prevState: State) => {
        const { isFocused, poll } = this.props;
        const { hasVideoEnded } = this.state;
        if (prevProps.isFocused !== isFocused) {
            if (isFocused) {
                if (this.videoRef?.current?.paused && !hasVideoEnded) {
                    this.tryPlayingVideo(this.videoRef.current);
                }
                Analytic.setPollQuestion(JSON.stringify(poll.questionText), poll.pollType || "");
            } else {
                if (!this.videoRef?.current?.paused) {
                    this.videoRef?.current?.pause();
                }
            }
        }
    };

    handleOptionSelected = (optionId: string) => {
        const { poll, onOptionSelected } = this.props;
        onOptionSelected?.(poll.id, optionId);
    };

    handleTrackingChange = async (checked: boolean) => {
        const { poll, onTrackingChange } = this.props;
        this.setState({ isTracking: checked });
        try {
            await onTrackingChange?.(poll.id, checked);
        } catch {
            this.setState({ isTracking: false });
        }
    };

    handlePollDelete = () => {
        const { poll, onPollDelete } = this.props;
        onPollDelete?.(poll.id);
    };

    handleVideoButtonClick = () => {
        if (this.videoRef?.current?.paused) {
            this.videoRef.current.currentTime = 0;
            this.props.cancelCardAutoSwipe?.();
            this.setState({ hasVideoEnded: false }, () => {
                if (this.videoRef?.current) this.tryPlayingVideo(this.videoRef.current);
            });
        } else {
            this.videoRef?.current?.pause();
            this.setState({ hasVideoEnded: true });
            const { poll, onVideoEnded } = this.props;
            onVideoEnded?.(poll.id);
        }
    };

    tryPlayingVideo = (video: HTMLVideoElement) => {
        video.play().catch((error) => {
            if (error.name === "NotAllowedError") {
                video.play();
            } else {
                video.load();
            }
        });
    };

    handleErrorOnVideo = (event: React.SyntheticEvent<HTMLVideoElement>) => {
        event.currentTarget.load();
    };

    handleLoadedData = (event: React.SyntheticEvent<HTMLVideoElement>) => {
        const { isFocused } = this.props;
        if (isFocused && event.currentTarget.paused) {
            this.tryPlayingVideo(event.currentTarget);
        }
    };

    handleVideoEnded = (event: React.SyntheticEvent<HTMLVideoElement>) => {
        this.setState({ hasVideoEnded: true });
        const { poll, onVideoEnded } = this.props;
        onVideoEnded?.(poll.id);
    };

    handleTimeUpdate = () => {
        this.setState((state) => ({ toggleRenderer: !state.toggleRenderer }));
    };

    render = () => {
        const {
            classes,
            poll,
            isFocused,
            remainingTime,
            availableHeight,
            mode,
            isDisplayDelete,
            showUserGuide,
            // cancelCardAutoSwipe,
        } = this.props;
        const { hasVideoEnded } = this.state;
        const { myVote: vote, durationToInteract, creator } = poll;
        const { isTracking } = this.state;
        const ratio = (availableHeight || 0) < HEIGHT_LOWER_THRESHOLD ? 1.25 : 1.5;
        let availableWidth = availableHeight ? availableHeight / ratio : undefined;
        if (availableWidth) {
            if (availableWidth > window.innerWidth) {
                availableWidth = window.innerWidth;
            }
            availableWidth -= 16;
        }
        const isVideo = poll.pollType === "video" ? true : false;
        const videoPoster = poll.thumbnail ? getMediaPath(poll.thumbnail) : undefined;

        return (
            <>
                {/* Delete Icon */}
                {mode === "YOURPOLL" && !!isDisplayDelete && (
                    <Grid
                        id={"btn-poll-delete"}
                        data-event="poll_delete"
                        sx={{
                            color: isVideo ? "#fff" : "#000",
                            position: "absolute",
                            zIndex: 1,
                            top: 0,
                            transform: `translateX(${(availableWidth || 0) / 2 - 4}px)`,
                        }}>
                        <img
                            className={classes.deleteImage}
                            onClick={this.handlePollDelete}
                            alt="delete"
                            src={
                                process.env.PUBLIC_URL +
                                "/images/delete-a3d165a6af3daa3dcb6c39bfe8bb9843.webp"
                            }
                        />
                    </Grid>
                )}
                <Paper
                    className={classNames(classes.pollContainer, {
                        focused: isFocused,
                    })}
                    sx={{
                        height: availableHeight ? availableHeight - 16 : undefined, // top and bottom 8px spacing
                        width: availableWidth,
                        background: isVideo ? "transparent" : "#fafafa",
                        boxShadow: "0px 0px 4px 0px rgba(0,0,0,0.5)",
                    }}>
                    {/* Bg Video */}
                    {poll.video && (
                        <>
                            <Grid
                                className={classes.videoContainer}
                                container
                                justifyContent="center">
                                <video
                                    disableRemotePlayback
                                    playsInline
                                    preload="metadata"
                                    ref={this.videoRef}
                                    poster={videoPoster}
                                    className={classes.optionMedia}
                                    onLoadedData={this.handleLoadedData}
                                    onError={this.handleErrorOnVideo}
                                    onEnded={this.handleVideoEnded}
                                    onTimeUpdate={this.handleTimeUpdate}>
                                    <source type="video/mp4" src={getMediaPath(poll.video)} />
                                </video>
                                {!hasVideoEnded && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            width: "80%",
                                            bottom: 4,
                                            zIndex: 1,
                                        }}>
                                        <LinearProgress
                                            variant="determinate"
                                            sx={{
                                                height: 2,
                                                borderRadius: 2,
                                                backgroundColor: (theme) => theme.palette.grey[500],
                                                "& .MuiLinearProgress-bar": {
                                                    borderRadius: 2,
                                                    backgroundColor: "white",
                                                },
                                            }}
                                            value={
                                                this.videoRef.current &&
                                                !this.videoRef.current.paused &&
                                                !!this.videoRef.current.duration
                                                    ? (this.videoRef.current.currentTime /
                                                          this.videoRef.current.duration) *
                                                      100
                                                    : 0
                                            }
                                        />
                                    </Box>
                                )}
                            </Grid>
                            {hasVideoEnded && <div className={classes.videoBackdrop}></div>}
                        </>
                    )}
                    {/* Poll */}
                    <Grid
                        container
                        direction="column"
                        sx={{
                            height: "100%",
                            transition: "all 1.5s ease",
                            padding: 1,
                        }}
                        wrap="nowrap">
                        <Grid item sx={{ zIndex: 1 }}>
                            {poll.pollType !== "follow" && (
                                <Grid
                                    container
                                    sx={{ mb: 0.5, mt: -0.5, color: isVideo ? "#fff" : "#000" }}>
                                    <Grid
                                        item
                                        xs={!!vote ? 8 : 4}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center">
                                        {creator && (
                                            <User
                                                creator={creator}
                                                name={creator?.name || "Anonymous"}
                                                variant={!!vote ? "row" : "column"}
                                                isVideo={isVideo}
                                                imageSrc={
                                                    !!creator?.image
                                                        ? getMediaPath(creator?.image)
                                                        : ""
                                                }
                                                mode={mode}
                                                isSponsored={poll.isSponsored}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={!!vote ? undefined : 4}>
                                        {!vote && mode !== "YOURPOLL" && (
                                            <Grid container direction="column" alignItems="center">
                                                <Grid item>
                                                    <Typography variant="caption" noWrap>
                                                        Flash ends in
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Timer
                                                        isVideo={isVideo}
                                                        remainingTime={remainingTime}
                                                        maxValue={durationToInteract}
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Grid
                                            container
                                            direction="column"
                                            alignItems={!!vote ? "flex-end" : "center"}>
                                            <Grid item>
                                                {mode !== "YOURPOLL" ? (
                                                    <Typography variant="caption" noWrap>
                                                        {!!vote
                                                            ? poll.totalVote + " Votes so far"
                                                            : "Votes so far"}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" noWrap>
                                                        {`Votes so far   ${poll.totalVote}`}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            {!vote && mode !== "YOURPOLL" && (
                                                <Grid item className={classes.mt}>
                                                    <Typography variant="subtitle2">
                                                        {poll.totalVote}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            {poll.pollType === "follow" && (
                                <Grid container sx={{ mb: 0.5, mt: -0.5 }}>
                                    <Grid
                                        item
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center">
                                        {creator && (
                                            <User
                                                creator={creator}
                                                name={creator?.name || "Anonymous"}
                                                variant="row"
                                                isVideo={isVideo}
                                                imageSrc={
                                                    !!creator?.image
                                                        ? getMediaPath(creator?.image)
                                                        : ""
                                                }
                                                mode="YOURPOLL"
                                                isSponsored={poll.isSponsored}
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                        <Grid item xs>
                            <Question
                                poll={poll}
                                hasVideoEnded={hasVideoEnded}
                                mode={mode}
                                availableHeight={availableHeight}
                                availableWidth={availableWidth}
                                isPollFocused={isFocused}
                                onOptionSelected={this.handleOptionSelected}
                                showUserGuide={showUserGuide}
                                onTrackingChange={this.handleTrackingChange}
                                onVideoButtonClick={this.handleVideoButtonClick}
                                isTracking={isTracking}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </>
        );
    };
}

const Poll = withStyles(styles)(Component);

export default Poll;
